/* eslint-disable */

import GrooveCards from 'api/GrooveCards'
import { v4 as uuidV4 } from 'uuid'
import config from 'config'
import storage from 'util/storage'
import {
  doMarkFetchingStatus,
  doUpdateAccessList,
  doRemoveAccessList,
} from 'actions/app'
import {
  doClearNonNamedTicketSearchResults,
  doClearTicketSearchResults,
} from 'actions/search'
import { doUpdateTicketDirtyStatus, doUpdateTickets } from 'actions/tickets'
import { doRealtimeNoteEvent } from 'actions/notes'
import {
  doFetchMailbox,
  doUpdateMailboxLocally,
  doRemoveMailbox,
} from 'actions/mailboxes'
import {
  doFetchAgent,
  doUpdateAgent,
  doRemoveAgent,
  doUpdateAgentTicketCollisionStatus,
} from 'actions/agents'

import * as types from 'constants/action_types'

import { selectAccountSubdomain } from 'selectors/app'
import { selectAgents } from 'selectors/agents/base'
import { selectMailboxIds } from 'selectors/mailboxes_base'
import {
  selectIsReplyingOrForwarding,
  selectIsAddingNote,
} from 'selectors/page'
import {
  selectTicketsById,
  selectRawById,
  selectCurrentTicketId,
  selectRawTicket,
} from 'selectors/tickets'
import { selectRequestIds } from 'selectors/requests'
import { selectCurrentUser } from 'ducks/currentUser/selectors'
import { selectIsEditorFocused } from 'ducks/editor'
import { selectCurrentTicketSearchQueryId } from 'selectors/search/base'

import realtime from 'util/realtime'
import { batchActions } from 'util/redux'
import WindowVisibility from 'util/window_visibility'
import { debounce } from 'util/functions'
import debug, { logError } from 'util/debug'
import debouncePromise from 'debounce-promise'
import { getVisibilityState } from 'util/browser'

import {
  ROOMS_REALTIME_LOAD_UNREAD,
  ROOMS_REALTIME_SYNC_UNREAD,
} from 'subapps/chat/actionTypes/rooms'

export function doUpdateRealtimeStatus(subscribed) {
  return {
    type: types.UPDATE_REALTIME_STATUS,
    data: {
      subscribed,
    },
  }
}

const actionMap = {
  ticket: { action: doRealtimeTicketEvent, triggerCardApi: true },
  changeset: { action: doRealtimeChangesetEvent, triggerCardApi: true },
  mailbox: { action: doRealtimeMailboxEvent, triggerCardApi: true },
  user: { action: doRealtimeUserEvent, triggerCardApi: true },
  settings: { action: doRealtimeSettingsEvent, triggerCardApi: true },
  global: { action: doRealtimeGlobalEvent, triggerCardApi: true },
  mailbox_access_list: {
    action: doRealtimeMailboxAccessListEvent,
    triggerCardApi: true,
  },
  comment: {
    action: doRealtimeNoteEvent,
  },
  agentAction: { action: doRealtimeAgentActionEvent, triggerCardApi: false },
  room: { action: doRealtimeRoomEvent, triggerCardApi: false },
}

const TYPING_TIMEOUT = 2000
// The leave timeout is 1 minute to ensure that other agents would'nt pickup tickets
// while an agent switches to a different tab to reference information related to the ticket
// Eg: Checking backend system for some data to respond to the customer
const LEAVE_TIMEOUT = 60000
const MAX_CONNECTION_ERRORS = 5
let connectionErrorCount = 0
let typingTimer = null
const REFETCH_COLLISIONS_INTERVAL = 300000 // 300 seconds, a.k.a. 5 minutes
let refetchCollisionsIntervalNum = null
let isFocused = true

async function startWatchingChannels(dispatch, channels) {
  try {
    await Promise.all(channels.map(channel => realtime.watch(channel)))
    dispatch(doUpdateRealtimeStatus(true))
    dispatch(doMarkFetchingStatus('subscribeRealtime', false))
  } catch (err) {
    leaveBreadcrumb('startWatchingChannels error', {
      error: err.message,
    })
    throw err
  }
}

// this function should only run once, because it's the socket that
// handles reconnections
export function doSubscribeToRealtime() {
  return async (dispatch, getState) => {
    const state = getState()
    const {
      app: { token },
    } = state
    leaveBreadcrumb('subscribing to realtime')

    try {
      dispatch(doMarkFetchingStatus('subscribeRealtime', true))
      const channels = await realtime.connect(
        token,
        message => watchHandler(message, dispatch),
        () => reconnectHandler('reconnect', dispatch, getState)
      )

      // Refetch collisions every N minutes
      if (refetchCollisionsIntervalNum) {
        clearInterval(refetchCollisionsIntervalNum)
      }

      realtime.runIntervalWhenConnected(
        () => reconnectHandler('interval', dispatch, getState),
        REFETCH_COLLISIONS_INTERVAL,
        'reconnectHandler'
      )

      WindowVisibility.addEventListener('focus', () => {
        leaveBreadcrumb('window focus')
        isFocused = true
        reconnectHandler('focus', dispatch, getState)
      })

      WindowVisibility.addEventListener('blur', () => {
        leaveBreadcrumb('window blur')
        const state = getState()
        const ticketId = selectCurrentTicketId(state)
        realtime.emit('viewTicket', {
          ticketId,
          phase: 'end',
          delay: LEAVE_TIMEOUT,
        })
        isFocused = false
      })

      await Promise.all([
        startWatchingChannels(dispatch, channels),
        fetchAndDispatchCollisionStatuses(dispatch),
        fetchAndDispatchUnreadRoomIds(dispatch),
      ])
    } catch (err) {
      leaveBreadcrumb('post-subscribe error', {
        error: err.message,
      })
      handleConnectionError(err, dispatch)
    }
  }
}

function handleConnectionError(err, dispatch) {
  leaveBreadcrumb('connectionError', {
    error: err.message,
  })
  connectionErrorCount += 1
  // NOTE (jscheel): Make sure we don't get into endless loop of reconnecting.
  if (connectionErrorCount > MAX_CONNECTION_ERRORS) {
    console.error(`Error connecting to realtime: ${err}`)
    dispatch(doMarkFetchingStatus('subscribeRealtime', true))
  } else {
    setTimeout(() => {
      dispatch(doUpdateRealtimeStatus(false))
      dispatch(doMarkFetchingStatus('subscribeRealtime', false))
    }, Math.round(Math.pow((20 + Math.random()) * connectionErrorCount, 2)))
  }
}

function watchHandler(message, dispatch) {
  const { meta } = message
  let type
  // skip comment_template, they are unsupported and it's not an error
  if (meta && meta.type === 'comment_template') return
  if (meta && meta.type && actionMap[meta.type]) {
    type = meta.type
  } else {
    console.warn('Cannot map realtime message to action', { message })
    if (bugsnagClient) {
      bugsnagClient.notify(
        {
          name: 'RealtimeActionMapError',
          message: 'Cannot map realtime message to action',
        },
        {
          metaData: {
            meta: message.meta,
          },
        }
      )
    }
    return
  }
  dispatch(actionMap[type].action(message))
  if (actionMap[type].triggerCardApi) {
    GrooveCards.triggerChangeCallback(message.meta)
  }
}

function leaveBreadcrumb(name, details) {
  if (bugsnagClient) bugsnagClient.leaveBreadcrumb(name, details)
  debug(name, details)
}

async function reconnectHandler(reason, dispatch, getState) {
  const realtimeWasConnected = realtime.isConnected()
  const started = new Date().getTime()
  const uuid = uuidV4()
  leaveBreadcrumb('reconnectHandler:start', {
    uuid,
    reason,
    isFocused,
    isConnected: realtimeWasConnected,
    state: realtime.getState(),
  })
  if (!isFocused) return
  if (!realtimeWasConnected) return
  try {
    await Promise.all([
      fetchAndDispatchUnreadRoomIds(dispatch),
      fetchAndDispatchCollisionStatuses(dispatch, uuid, () => {
        leaveBreadcrumb('reconnectHandler:success', {
          uuid,
          reason,
          realtimeWasConnected,
          isFocused,
          realtimeIsConnected: realtime.isConnected(),
          state: realtime.getState(),
        })
      }),
    ])
    dispatch(doRealtimeSetCurrentAgentCollisionStatus())
  } catch (err) {
    const realtimeIsConnected = realtime.isConnected()
    const payload = {
      uuid,
      reason,
      realtimeWasConnected,
      realtimeIsConnected,
      isFocused,
      state: realtime.getState(),
    }
    leaveBreadcrumb('reconnectHandler:error', payload)
    // don't report if it failed because realtime disconnected
    // or because the window is unfocused, these issues are expected
    if (!isFocused || !realtimeIsConnected) return
    logError(err)
    bugsnagClient.notify(err, {
      severity: 'error',
      metaData: payload,
    })
  }
}

function fetchCollisionStatuses(uuid) {
  return new Promise((resolve, reject) => {
    const requestId = uuid || uuidV4()
    realtime.emit('fetchCollisionStatuses', { requestId }, (err, statuses) => {
      if (err) {
        leaveBreadcrumb('fetchCollisionStatuses Error', {
          requestId,
          errorMessage: err.message,
        })
        console.error(err)
        reject(err)
      } else {
        resolve(statuses)
      }
    })
  })
}

function fetchUnreadRoomIds(uuid) {
  return new Promise((resolve, reject) => {
    const requestId = uuid || uuidV4()
    realtime.emit('fetchUnreadRooms', { requestId }, (err, unreadRoomIds) => {
      if (err) {
        leaveBreadcrumb('fetchUnreadRooms Error', {
          requestId,
          errorMessage: err.message,
        })
        console.error(err)
        reject(err)
      } else {
        resolve(unreadRoomIds)
      }
    })
  })
}

async function _fetchAndDispatchCollisionStatuses(dispatch, uuid, onComplete) {
  try {
    const statuses = await fetchCollisionStatuses(uuid)
    if (onComplete) onComplete(statuses)
    dispatch({
      type: types.FETCH_AGENT_COLLISION_STATUSES_SUCCESS,
      data: statuses,
    })
  } catch (err) {
    leaveBreadcrumb('post-subscribe fetchCollisionStatuses error', {
      error: err.message,
    })
    throw err
  }
}

async function _fetchAndDispatchUnreadRoomIds(dispatch) {
  try {
    const isLiveChatEnabled =
      storage.get('isLiveChatEnabled') || config.isDevelopment || config.isAlpha
    if (!isLiveChatEnabled) return
    const unreadRoomIds = await fetchUnreadRoomIds()
    dispatch({
      type: ROOMS_REALTIME_LOAD_UNREAD,
      payload: unreadRoomIds,
    })
  } catch (err) {
    leaveBreadcrumb('post-subscribe fetchUnreadRoomIds error', {
      error: err.message,
    })
    throw err
  }
}

const fetchAndDispatchCollisionStatuses = debouncePromise(
  _fetchAndDispatchCollisionStatuses,
  500
)
const fetchAndDispatchUnreadRoomIds = debouncePromise(
  _fetchAndDispatchUnreadRoomIds,
  500
)

const mapMessageTypeToAction = message => {
  const { meta } = message
  if (meta && meta.type) {
    return actionMap[type]
  }
}

const findPendingRequest = (optimist = [], changesetId) =>
  optimist.find(
    entry =>
      entry.action &&
      entry.action.optimist &&
      entry.action.optimist.id === changesetId
  )

function doRealtimeTicketEvent(message) {
  return function(dispatch, getState) {
    const {
      meta: {
        changeset_id: changesetId,
        account_ticket_number: ticketId,
        request_id: requestId,
        action,
      },
    } = message
    const state = getState()
    const requestIds = selectRequestIds(state)
    // Skip the realtime update if we made the request
    if (requestIds.includes(requestId)) return false
    // Skip the realtime update if we are expecting an API response
    if (findPendingRequest(state.optimist, changesetId)) return false
    const currentUser = selectCurrentUser(state)
    const ticket = realtime.buildTicketFromMessage(message, currentUser)

    switch (action) {
      case 'destroy':
        dispatch({
          type: types.DELETE_TICKETS_SUCCESS,
          data: {
            tickets: [ticket],
          },
          meta: { requestId },
        })
        break
    }
  }
}

function doRealtimeRoomEvent(message) {
  console.log({ message, ROOMS_REALTIME_SYNC_UNREAD })
  return dispatch => {
    const {
      data: { roomId, isRead },
      meta: { type, action },
    } = message

    return dispatch({
      type: ROOMS_REALTIME_SYNC_UNREAD,
      payload: {
        roomId,
        isRead,
      },
    })
  }
}

// NOTE (mbunsch) this storage is temporary to verify this
// solution works. Once confirmed, it should be moved to redux
// store
const changesetQueue = {}
const changesetQueueTimeouts = {}

const attemptToRebuildEventHistory = (ticketId, dispatch) => {
  const queue = changesetQueue[ticketId]

  // build a lookup hash to build a linked list
  const lookup = {}
  queue.forEach(item => (lookup[item.changesetId] = item))

  // build the linked list
  const list = [queue[0]] // 0 is always the changeset we know
  let current = list[0]
  while (current) {
    current = lookup[current.previousChangesetId]
    if (current) {
      list.push(current)
      // remove a changeset that was used already, prevent infinite loops
      delete lookup[current.previousChangesetId]
    }
  }

  // check if the linked list is complete
  if (queue.length != list.length) return

  // filter out queue items without messages to only leave messages
  // that can be re-dispatched
  const messages = list.map(s => s.message).filter(i => !!i)

  // as we have the correct order now, clear storage
  if (changesetQueueTimeouts[ticketId]) {
    clearTimeout(changesetQueueTimeouts[ticketId])
    delete changesetQueueTimeouts[ticketId]
  }
  delete changesetQueue[ticketId]

  // finally, redispatch the actions in correct order
  dispatch(doReRunRealtimeChangesetEvents(ticketId, messages))
}

function doReRunRealtimeChangesetEvents(ticketId, messages) {
  return function(dispatch, getState) {
    const state = getState()
    const currentUser = selectCurrentUser(state)
    const actions = []

    actions.push(doClearNonNamedTicketSearchResults())
    actions.push(doUpdateTicketDirtyStatus(ticketId, true))

    messages.forEach(message => {
      const ticket = realtime.buildTicketFromMessage(message, currentUser)
      if (ticket.diff) {
        actions.push(doUpdateTickets([ticket]))
      }
    })
    dispatch(batchActions(actions))
  }
}

const bufferFlushDelay = 2000
const bufferMaxSize = 25
let bufferFlushInterval = null
let bufferedTickets = []

function flushBuffer(dispatch, options) {
  const tickets = bufferedTickets
  bufferedTickets = []
  dispatch(doUpdateTickets(tickets, options))
}

function bufferedDispatch(dispatch, ticket, options) {
  bufferedTickets.push(ticket)
  // if the buffer hits upper bound, flush it
  if (bufferedTickets.length >= bufferMaxSize) {
    flushBuffer(dispatch, options)
    return
  }
  if (bufferFlushInterval) clearInterval(bufferFlushInterval)
  bufferFlushInterval = setTimeout(() => {
    flushBuffer(dispatch, options)
  }, bufferFlushDelay)
}

function doRealtimeChangesetEvent(message) {
  return function(dispatch, getState) {
    const {
      meta: {
        timestamp,
        changeset_id: changesetId,
        previous_changeset_id: previousChangesetId,
        account_ticket_number: ticketId,
        request_id: requestId,
      },
    } = message

    const state = getState()
    const requestIds = selectRequestIds(state)
    const subdomain = selectAccountSubdomain(state)
    const shouldReorderChangesets = false

    // Skip the realtime update if we made the request
    if (requestIds.includes(requestId)) return false
    // Skip the realtime update if we are expecting an API response
    if (findPendingRequest(state.optimist, changesetId)) return false
    const actions = []
    actions.push(
      doClearNonNamedTicketSearchResults({
        keepCurrent: true,
        refetchListOnNextUpdate: true,
      })
    )

    const currentUser = selectCurrentUser(state)
    const ticket = realtime.buildTicketFromMessage(message, currentUser)
    const ticketInStore = selectRawTicket(state, ticket.id)
    if (
      ticket &&
      ticketInStore &&
      ticketInStore.updated_at >= ticket.updated_at
    ) {
      if (config.isDevelopment) {
        console.debug(`Realtime update for ${ticket.id} is stale, dropping`, {
          inStore: ticketInStore.updated_at,
          inMessage: ticket.updated_at,
        })
      }
      return false
    }

    if (ticket.diff) {
      bufferedDispatch(dispatch, ticket, {
        currentQueryId: selectCurrentTicketSearchQueryId(state),
      })
    }
  }
}

function doRealtimeMailboxEvent(message) {
  return function(dispatch, getState) {
    const {
      data: mailbox,
      meta: { action, user_ids },
    } = message
    if (!mailbox.id) {
      // TODO (jscheel): Determine if we want to report this bugsnag.
      return
    }
    const state = getState()
    switch (action) {
      case 'create':
        dispatch(doFetchMailbox(mailbox.id))
        break
      case 'update':
        if (selectMailboxIds(state).indexOf(mailbox.id) === -1) {
          dispatch(doFetchMailbox(mailbox.id))
        } else {
          const currentUserId = selectCurrentUser(state).id
          if (
            user_ids &&
            user_ids.length > 0 &&
            user_ids.indexOf(currentUserId) === -1
          ) {
            dispatch(doRemoveMailbox(mailbox.id))
          } else {
            dispatch(doUpdateMailboxLocally(mailbox))
          }
        }
        break
      case 'destroy':
        dispatch(doRemoveMailbox(mailbox.id))
        break
      default:
        console.warn('Unknown message action')
        break
    }
  }
}

function doRealtimeMailboxAccessListEvent(message) {
  return function(dispatch, getState) {
    const {
      data: access_list,
      meta: { action, mailbox_id },
    } = message
    const state = getState()
    switch (action) {
      case 'create':
      case 'update':
        // NOTE (jscheel): Access lists are so basic that we don't need to fetch
        // them separately right now. For now, the reducer will simply create
        // a new access list if one does not exist yet.
        dispatch(
          doUpdateAccessList({
            mailboxId: mailbox_id,
            agentIds: access_list.agent_ids,
          })
        )
        break
      case 'destroy':
        // NOTE (jscheel): Access lists do not have their own id right now, so
        // we search for them via their mailbox id.
        dispatch(doRemoveAccessList(mailbox_id))
        break
      default:
        console.warn('Unknown message action')
        break
    }
  }
}

const agentIsMissing = (state, user) => {
  return (
    selectAgents(state)
      .map(agent => agent.id)
      .indexOf(user.id) === -1
  )
}

function doRealtimeUserEvent(message) {
  return function(dispatch, getState) {
    const {
      data: user,
      meta: { action, role },
    } = message
    if (!user.id) return
    const state = getState()
    switch (action) {
      case 'create':
        if (role === 'agent') {
          dispatch(doFetchAgent(user.id))
        }
        break
      case 'update':
        // NOTE (jscheel): Right now we only deal with agent events.
        if (role === 'agent') {
          if (user.archived) {
            dispatch(doRemoveAgent(user.id))
          } else if (agentIsMissing(state, user)) {
            dispatch(doFetchAgent(user.id))
          } else {
            dispatch(doUpdateAgent(user))
          }
        }
        break
      case 'destroy':
        dispatch(doRemoveAgent(user.id))
        break
      default:
        console.warn('Unknown message action', message)
        break
    }
  }
}

function doRealtimeSettingsEvent(message) {
  return () => {
    console.log(message)
  }
}

function doRealtimeGlobalEvent(message) {
  return () => {
    console.log(message)
  }
}

const agentLeaveTimers = {}

function doRealtimeAgentActionEvent(message) {
  return function(dispatch) {
    const {
      data,
      meta: { action, delay },
      createdAt,
    } = message
    const { ticketId, agentId } = data
    const existingLeaveTimer = agentLeaveTimers[agentId]
    if (existingLeaveTimer) {
      clearTimeout(existingLeaveTimer)
    }
    const timer = setTimeout(() => {
      dispatch(
        doUpdateAgentTicketCollisionStatus(action, agentId, ticketId, createdAt)
      )
    }, delay)
    if (action === 'viewTicket' && ticketId === null && delay && delay > 0) {
      agentLeaveTimers[agentId] = timer
    }
  }
}

const startTyping = debounce(
  ticketId => {
    realtime.emit('typeOnTicket', {
      ticketId,
      phase: 'begin',
    })
  },
  500,
  true
)

export function doRealtimeAgentStartTicketTypingNotification(
  ticketId,
  ignoreFocus = false
) {
  return function(dispatch, getState) {
    const state = getState()
    const isEditorFocused = selectIsEditorFocused(state)
    // skip sending event because editor doesnt have focus
    if (!isEditorFocused && !ignoreFocus) return
    if (typingTimer) {
      clearTimeout(typingTimer)
    }
    startTyping(ticketId)
    typingTimer = setTimeout(() => {
      realtime.emit('typeOnTicket', {
        ticketId,
        phase: 'end',
      })
      if (typingTimer) {
        clearTimeout(typingTimer)
      }
    }, TYPING_TIMEOUT)
  }
}

export function doRealtimeSetCurrentAgentCollisionStatus() {
  return (dispatch, getState) => {
    const state = getState()
    // TODO (jscheel): What to do about isMerging?
    const currentTicketId = selectCurrentTicketId(state)
    const isReplying = selectIsReplyingOrForwarding(state)
    const isAddingNote = selectIsAddingNote(state)
    const isEditorFocused = selectIsEditorFocused(state)
    // When the browser is loading in the background, dont emit any
    // being realtime events
    const isBrowserVisible = getVisibilityState() === 'visible'
    if (currentTicketId && isBrowserVisible) {
      if (isReplying && isEditorFocused) {
        return realtime.emit('replyToTicket', {
          ticketId: currentTicketId,
          phase: 'begin',
        })
      }
      if (isAddingNote && isEditorFocused) {
        return realtime.emit('addNote', {
          ticketId: currentTicketId,
          phase: 'begin',
        })
      }
      return realtime.emit('viewTicket', {
        ticketId: currentTicketId,
        phase: 'begin',
      })
    }
  }
}
